import generatePicker from 'antd/es/date-picker/generatePicker';
import type { DateTime } from 'luxon';
import luxonGenerateConfig from 'rc-picker/es/generate/luxon';
import { istNow } from '@/lib/istNow';
import 'antd/es/date-picker/style';

/**
 * AntD's DatePicker, backed by LUXON instead of dayjs.
 *
 * CLAUDE.md mandates luxon for all date/time work — no dayjs, no moment, no mixing.
 * AntD 5's stock `DatePicker` hands you a `Dayjs` in `onChange`, which would put a
 * second date library in the application's own code and leave every form converting
 * between the two. Two date libraries disagree about DST boundaries and about what
 * "end of day" means, and they disagree in production, months later.
 *
 * `generatePicker` is AntD's supported way to swap the date backend, so this is the
 * documented path rather than a workaround. Import THIS, never `antd`'s DatePicker.
 *
 * (dayjs remains a transitive dependency of antd itself. That is antd's internal
 * business; what matters is that no PAMS module imports it — ESLint enforces that.)
 *
 * ── Import the `es/` build, never `lib/` ──────────────────────────────────────────
 * rc-picker ships both, with no `exports` map to pick between them. `lib/` is CJS
 * compiled by babel, so `import config from 'rc-picker/lib/generate/luxon'` yields
 * `{ __esModule: true, default: config }` — the config one level too deep. The picker
 * then calls `generateConfig.getNow()` on the wrapper and dies with
 * `e.getNow is not a function`, at RENDER time, in the production bundle only.
 * `es/` is real ESM and resolves to the config itself. It is also what antd's own ES
 * build imports, so this keeps a single rc-picker instance in the bundle.
 *
 * ── ⚠️ [P7C §6a] `getNow` IS IST, NOT THE BROWSER'S ZONE ──────────────────────────
 * rc-picker's luxon config answers `getNow` with `DateTime.now()`, the BROWSER's zone, and
 * the picker reads it for its always-on "Today" button and for the day it highlights. On a
 * laptop set to UTC, from 18:30 to 24:00 UTC that is YESTERDAY — so "Today" filled in a date
 * publish's past-date blocker (IST, `istDutyDate`) then refused. Overriding the one function
 * makes the button and the highlight the IST day; every other function in the config operates
 * on the value it is handed and needs no zone of its own.
 */
export const DatePicker = generatePicker<DateTime>({ ...luxonGenerateConfig, getNow: istNow });

export type { DateTime };
